import React from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import { styled } from '@mui/system';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled(Box)({
  width: "100vw",
  maxWidth: "100%",
  minHeight: "100vh",
  backgroundColor: "#000000",
  color: "#FFFFFF",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
});

const Header = styled(Box)({
  width: "100%",
  padding: "16px 24px",
});

const HeaderContent = styled(Box)({
  maxWidth: "800px",
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const Logo = styled(Box)({
  display: "flex",
  alignItems: "center",
  fontSize: "18px",
  fontWeight: "600",
  color: "#FFFFFF",
  gap: "8px",
  cursor: "pointer",
});

const MainContainer = styled(Box)({
  maxWidth: "600px",
  margin: "0 auto",
  padding: "40px 24px",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  minHeight: "calc(100vh - 200px)",
});

const ContactSection = styled(Box)({
  backgroundColor: "#1F2937",
  borderRadius: "12px",
  padding: "32px",
  textAlign: "center",
});

const ContactTitle = styled(Typography)({
  fontSize: "36px",
  fontWeight: "700",
  color: "#FFFFFF",
  marginBottom: "16px",
  lineHeight: "1.1",
});

const ContactSubtitle = styled(Typography)({
  fontSize: "18px",
  color: "#9CA3AF",
  marginBottom: "32px",
  fontWeight: "400",
});

const ContactForm = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  textAlign: "left",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#374151",
    borderRadius: "8px",
    color: "#FFFFFF",
    "& fieldset": {
      borderColor: "#4B5563",
    },
    "&:hover fieldset": {
      borderColor: "#6B7280",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#10B981",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#9CA3AF",
    "&.Mui-focused": {
      color: "#10B981",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "#FFFFFF",
  },
});

const SubmitButton = styled(Button)({
  backgroundColor: "#10B981",
  color: "#FFFFFF",
  borderRadius: "10px",
  padding: "16px 32px",
  fontSize: "16px",
  fontWeight: "600",
  textTransform: "none",
  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#059669",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.35)",
  },
});

const Footer = styled(Box)({
  textAlign: "center",
  padding: "32px 24px",
  color: "#6B7280",
  fontSize: "14px",
  maxWidth: "800px",
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
});

const FooterLink = styled(Typography)({
  fontSize: "14px",
  color: "#9CA3AF",
  cursor: "pointer",
  fontWeight: "400",
  "&:hover": {
    color: "#FFFFFF",
  },
});

const Bullet = styled(Typography)({
  fontSize: "14px",
  color: "#6B7280",
  userSelect: "none",
});

const ContactPage = () => {
  const navigate = useNavigate();

  return (
    <PageContainer>
      <Header>
        <HeaderContent>
          <Logo onClick={() => navigate('/')}>
            🍃
            Odessa
          </Logo>
        </HeaderContent>
      </Header>

      <MainContainer>
        <ContactSection>
          <ContactTitle>Get in Touch</ContactTitle>
          <ContactSubtitle>
            Have questions about your recovery journey? We're here to help.
          </ContactSubtitle>
          
          <ContactForm>
            <StyledTextField
              label="Name"
              variant="outlined"
              fullWidth
            />
            <StyledTextField
              label="Email"
              variant="outlined"
              type="email"
              fullWidth
            />
            <StyledTextField
              label="Subject"
              variant="outlined"
              fullWidth
            />
            <StyledTextField
              label="Message"
              variant="outlined"
              multiline
              rows={4}
              fullWidth
            />
            <SubmitButton
              startIcon={<EmailIcon />}
            >
              Send Message
            </SubmitButton>
          </ContactForm>
        </ContactSection>
      </MainContainer>

      <Footer>
        <FooterLink onClick={() => navigate('/')}>Home</FooterLink>
        <Bullet>•</Bullet>
        <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
          Odessa
        </Typography>
        <Bullet>•</Bullet>
        <FooterLink>Support</FooterLink>
      </Footer>
    </PageContainer>
  );
};

export default ContactPage;